import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMe } from '../services/api';

function decodeToken(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await getMe();
      setUser(res.data);
    } catch {
      // Había un token guardado y dejó de ser válido (sesión de 48hs vencida,
      // o revocada) — a diferencia de nunca haber logueado. Marca leída por
      // Login.js para explicar el porqué en vez de mostrar el form en blanco.
      sessionStorage.setItem('session_expired', '1');
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setUser(null);
    window.location.href = '/';
  }, []);

  // Leer role también del token para acceso inmediato antes de que /me responda
  // Solo usar si el token no está vencido
  const tokenData = (() => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const decoded = decodeToken(token);
    if (!decoded?.exp) return null;
    return decoded.exp > Date.now() / 1000 ? decoded : null;
  })();

  const role = user?.role ?? tokenData?.role ?? null;
  const doctorId = user?.doctor_id ?? tokenData?.doctor_id ?? null;
  const clinicId = user?.clinic_id ?? tokenData?.clinic_id ?? null;
  // acting_as_support solo viene de /users/me (user), no está en el JWT
  // decodificado acá — no hace falta antes de que /me responda, a
  // diferencia de role/clinic_id que sí se usan para gatear rutas.
  const actingAsSupport = !!user?.acting_as_support;

  return (
    <AuthContext.Provider value={{
      user,
      role,
      loading,
      isAdmin: role === 'admin',
      isRecepcionista: role === 'recepcionista',
      isMedico: role === 'medico',
      // Acceso externo de solo lectura a Algoritmos publicados — ver
      // guard central en backend/auth.py get_current_user.
      isInvitado: role === 'invitado',
      // Control total cross-clínica para soporte — ver routers/superadmin.py.
      isSuperadmin: role === 'superadmin',
      actingAsSupport,
      doctorId,
      clinicId,
      reload: loadUser,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
}
